import { useEffect, useState } from "react";
import MetamaskService from "../../utils/metamask.service";
import ModalService from "../../utils/modal.service";
import Modal from "../Modal/Modal.component";
import styles from "./ConnectWallet.module.scss";
import ToastService from "../../utils/toast.service";
import { useAccountEffect } from "wagmi";
import { getAccount } from "@wagmi/core";
import CookieService from "../../utils/cookie.service";
import HelperService from "../../utils/helper.service";
import { ethers } from "ethers";
import { metadata, networks, projectId, themeVariables, wagmiAdapter } from "../../utils/walletConnect.service";
import { createAppKit, useAppKitTheme, useDisconnect } from "@reown/appkit/react";

//TODO UPDATE THIS IMPORT
import { AccountType, Chain, Store, useStore } from "../../utils/store.service";
import FtsoService from "../../utils/ftso.service";
/* ----- */

export const appKit = createAppKit({
  adapters: [wagmiAdapter],
  networks,
  projectId,
  metadata,
});



interface IProps {
  buttonStyle?: string;
  hide?: boolean; // A hacky solution where we can't access the modal if we reroute from a page with a WalletConnect component.
  customId?: string;
}

const ConnectWallet: React.FC<IProps> = ({ buttonStyle, hide }) => {
  const [input, setInput] = useState("");
  const [inputError, setInputError] = useState(false);
  const { account, accountType, accountConnected } = useStore();
  const disconnectWalletConnect = useDisconnect();

  const walletModal = "walletConnectModal";
  const addressModal = "addressModal";

  // useWeb3ModalEvents((event) => console.error(event))
  useEffect(() => {
    const connectEthereumProvider = async (accountType: AccountType) => {
      switch (accountType) {
        case AccountType.MetaMask:
          if (window.ethereum && window.ethereum.isMetaMask) {
            connectWithMetaMask();
          } else {
            console.error("Account disconnected");
            FtsoService.setAccount("", AccountType.NONE);
          }
          break;
        case AccountType.WalletConnect:
          const account = getAccount(wagmiAdapter.wagmiConfig);
          if (account.isConnected) {
            // ...
          } else {
            console.error("Account disconnected");
            FtsoService.setAccount("", AccountType.NONE);
          }
          break;
        case AccountType.Simulator:
        case AccountType.NONE:
        default:
          break;
      }
    };

    if (FtsoService.initialized) {
      connectEthereumProvider(accountType.get());
    }
  }, [FtsoService.initialized]);

  FtsoService.listen("connectWallet", () => {
    accountConnected.set(FtsoService.ACCOUNT_IS_CONNECTED);
    account.set(FtsoService.ACCOUNT);
  });

  const switchToAddress = () => {
    ModalService.closeModal(walletModal);
    ModalService.openModal(addressModal);
  };

  const switchToWallet = () => {
    ModalService.closeModal(addressModal);
    ModalService.openModal(walletModal);
  };

  const networkChangeInsideWallet = (chain: number) => {
    const chainName: Chain = HelperService.chainFromId(chain);
    FtsoService.setChain(chainName);
  };

  const handleChainChanged = (chainString: string) => {
    const chainNumber: number = Number(chainString);
    ToastService.info("Metamask chain changed.", undefined, {
      toastId: "toast-chain-change",
    });
    networkChangeInsideWallet(chainNumber);
  };

  const connectWithMetaMask = () => {
    if (window.ethereum) {
      MetamaskService.connectWithMetaMask((account) => {
        FtsoService.setAccount(account, AccountType.MetaMask);
        ModalService.closeModal(walletModal);
      });
      window.ethereum.on("accountsChanged", handleAccountChanged);
      window.ethereum.on("chainChanged", handleChainChanged);
      window.ethereum.on("disconnect", handleDisconnect);
    } else {
      ToastService.error(
        "Metamask not detected.",
        "Please install metamask plugin or use browser that has it installed.",
        {
          autoClose: false,
        }
      );
    }
  };

  const connectAnotherAccount = () => {
    if (window.ethereum) {
      MetamaskService.connectAnotherAccount(() => {
        ModalService.closeModal(walletModal);
      });
    } else {
      ToastService.error(
        "Metamask not detected.",
        "Please install metamask plugin or use browser that has it installed.",
        {
          autoClose: false,
        }
      );
    }
  };

  const disconnect = async () => {
    // If the Account type is MetaMask, unregister listeners
    if (accountType.get() === AccountType.MetaMask && window.ethereum) {
      window.ethereum.removeListener("accountsChanged", handleAccountChanged);
      window.ethereum.removeListener("chainChanged", handleChainChanged);
      window.ethereum.removeListener("disconnect", handleDisconnect);
    }
    await disconnectWalletConnect.disconnect();
    FtsoService.setAccount("", AccountType.NONE);
    ModalService.closeModal(walletModal);
  };

  const connectWithReadOnly = () => {
    setInputError(false);
    try {
      const trimmed = ethers.utils.getAddress(input.trim());
      FtsoService.setAccount(trimmed, AccountType.Simulator);
      ModalService.closeModal(addressModal);
      setInput("");
    } catch (e) {
      setInputError(true);
    }
  };

  useAccountEffect({
    onConnect({ address, connector, isReconnected }) {
      if (isReconnected) {
        return;
      }
      console.log("Connected", { address, connector, isReconnected });
      if (address) {
        if (
          Store.accountConnected.get() &&
          Store.accountType.get() === AccountType.WalletConnect &&
          address !== CookieService.getCookie(CookieService.COOKIE_ACCOUNT)
        ) {
          ToastService.info(
            "Address refreshed",
            "Your connected address may have changed, check if it is still correct.",
            {
              autoClose: false,
            }
          );
        }

        FtsoService.setAccount(address, AccountType.WalletConnect);
        ModalService.closeModal(walletModal);

        if (connector) {
          Store.connector.set(connector);
        }
      } else {
        setInputError(true);
      }
    },
    onDisconnect() {
      console.log("Disconnected");
      FtsoService.setAccount("", AccountType.NONE);
      const connector = Store.connector.get();
      if (connector) {
        Store.connector.set(undefined);
      }
    },
  });

  const themeManager = useAppKitTheme();

  const connectWithWalletConnect = async () => {
    themeVariables["--w3m-overlay-background-color"] = "";
    themeVariables["--w3m-overlay-backdrop-filter"] = "";
    themeManager.setThemeVariables(themeVariables);
    await appKit.open();
    // ModalService.closeModal(walletModal)
  };

  const openCorrectModal = async () => {
    if (
      accountConnected.get() &&
      accountType.get() === AccountType.WalletConnect
    ) {
      themeVariables["--w3m-overlay-background-color"] =
        "var(--quinary-transp-25)";
      themeVariables["--w3m-overlay-backdrop-filter"] = "blur(10px)";
      themeManager.setThemeVariables(themeVariables);
      await appKit.open();
    } else {
      ModalService.openModal(walletModal);
    }
  };

  const handleDeleteCookies = () => {
    CookieService.deleteCookies();
    window.location.reload();
  };

  return (
    <>
      {hide ? (
        <></>
      ) : (
        <div
          className={buttonStyle ? styles[buttonStyle] : styles.button}
          onClick={() => openCorrectModal()}
        >
          {account.get().length ? (
            <>
              {HelperService.shortenAddress(account.get())}
              <img
                src={`${import.meta.env.VITE_BASE_PATH}/images/connect/${accountType.get()}.svg`}
                alt="type"
              />
            </>
          ) : (
            <>Connect wallet</>
          )}
        </div>
      )}
      <Modal
        id={walletModal}
        onOverlayClick={() => ModalService.closeModal(walletModal)}
      >
        <div className={styles.modal}>
          <h3>
            Connect wallet?
            <img
              src={`${import.meta.env.VITE_BASE_PATH}/images/close-gray.svg`}
              onClick={() => ModalService.closeModal(walletModal)}
              alt="close"
            />
          </h3>
          {!accountConnected.get() && (
            <>
              <button
                className={styles.connect}
                onClick={() => connectWithMetaMask()}
              >
                <div className={styles.content}>
                  <div>MetaMask</div>
                  <span>Connect with MetaMask</span>
                </div>
                <img
                  src={`${import.meta.env.VITE_BASE_PATH}/images/connect/MetaMask.svg`}
                  alt="MetaMask"
                />
              </button>
              {/* Uncomment the code below to enable WalletConnect button */}
              <button
                className={styles.connect}
                onClick={() => connectWithWalletConnect()}
              >
                <div className={styles.content}>
                  <div>WalletConnect</div>
                  <span>Connect with WalletConnect</span>
                </div>
                <img
                  src={`${import.meta.env.VITE_BASE_PATH}/images/connect/WalletConnect.svg`}
                  alt="WalletConnect"
                />
              </button>
              <button
                className={styles.simulate}
                onClick={() => switchToAddress()}
              >
                <div className={styles.content}>
                  <div>Enter address</div>
                  <span>Read-only wallet</span>
                </div>
                <img
                  src={`${import.meta.env.VITE_BASE_PATH}/images/connect/read-only.svg`}
                  alt="Eye"
                />
              </button>
            </>
          )}
          {accountConnected.get() &&
            accountType.get() === AccountType.MetaMask && (
              <>
                <button
                  className={styles.copy}
                  onClick={() =>
                    HelperService.copyAddressToClipboard(account.get())
                  }
                >
                  <div className={styles.content}>
                    <div>Copy address</div>
                    <span>Copy address to clipboard</span>
                  </div>
                  <svg
                    height="30px"
                    width="32px"
                    fill="var(--mid-dark)"
                    viewBox="0 0 45 45"
                  >
                    <use
                      href={`${import.meta.env.VITE_BASE_PATH}/images/copy_content.svg#copyIcon`}
                    ></use>
                  </svg>
                </button>

                <button
                  className={styles.connect}
                  onClick={() => connectAnotherAccount()}
                >
                  <div className={styles.content}>
                    <div>Another account</div>
                    <span>Connect with MetaMask</span>
                  </div>
                  <img
                    src={`${import.meta.env.VITE_BASE_PATH}/images/connect/MetaMask.svg`}
                    alt="MetaMask"
                  />
                </button>

                <button
                  className={styles.disconnect}
                  onClick={() => disconnect()}
                >
                  <div className={styles.content}>
                    <div>Disconnect</div>
                    <span>Disconnect MetaMask</span>
                  </div>
                  <img
                    src={`${import.meta.env.VITE_BASE_PATH}/images/connect/MetaMask.svg`}
                    alt="MetaMask"
                  />
                </button>
              </>
            )}
          {accountConnected.get() &&
            accountType.get() === AccountType.Simulator && (
              <>
                <button
                  className={styles.copy}
                  onClick={() =>
                    HelperService.copyAddressToClipboard(account.get())
                  }
                >
                  <div className={styles.content}>
                    <div>Copy address</div>
                    <span>Copy address to clipboard</span>
                  </div>
                  <svg height="30px" width="32px" viewBox="0 0 45 45">
                    <use
                      href={`${import.meta.env.VITE_BASE_PATH}/images/copy_content.svg#copyIcon`}
                    ></use>
                  </svg>
                </button>

                <button
                  className={styles.disconnect}
                  onClick={() => disconnect()}
                >
                  <div className={styles.content}>
                    <div>Disconnect</div>
                    <span>Disconnect read-only wallet</span>
                  </div>
                  <img
                    src={`${import.meta.env.VITE_BASE_PATH}/images/connect/read-only.svg`}
                    alt="Read only"
                  />
                </button>
              </>
            )}
        </div>
        <p onClick={handleDeleteCookies} className={styles.disclaimer}>
          Having issues? Clear cookies.
        </p>
      </Modal>
      <Modal
        id={addressModal}
        onOverlayClick={() => ModalService.closeModal(addressModal)}
      >
        <div className={styles.modal}>
          <h3>
            <img
              src={`${import.meta.env.VITE_BASE_PATH}/images/arrow-left.svg`}
              onClick={() => switchToWallet()}
              alt="back"
            />
            Enter address
          </h3>
          <div className={styles.address}>
            <label>Public address</label>
            <input
              type="text"
              value={input}
              onInput={(e: React.ChangeEvent<HTMLInputElement>) =>
                setInput(e.target.value)
              }
              placeholder="Paste public address 0x..."
            />
            {inputError && (
              <div className={styles.error}>
                Please enter valid public address (0x).
              </div>
            )}
          </div>
          <button
            className={styles.connect}
            onClick={() => connectWithReadOnly()}
          >
            <div className={styles.content}>
              <div>Connect</div>
              <span>Read-only wallet</span>
            </div>
            <img
              src={`${import.meta.env.VITE_BASE_PATH}/images/connect/read-only.svg`}
              alt="Eye"
            />
          </button>
          <a
            href="/learn/How-to-use-flare_space"
            onClick={() => ModalService.closeModal(addressModal)}
          >
            Don&apos;t know how to connect?
          </a>
        </div>
        <p onClick={handleDeleteCookies} className={styles.disclaimer}>
          Having issues? Clear cookies.
        </p>
      </Modal>
    </>
  );
};

function handleAccountChanged(accounts: string[]) {
  if (accounts.length) {
    if (
      Store.account.get() &&
      Store.accountType.get() === AccountType.MetaMask
    ) {
      FtsoService.setAccount(accounts[0], AccountType.MetaMask);
    } else {
      console.warn(
        "Account type is not Metamask, not changing connected account!"
      );
    }
  } else {
    FtsoService.setAccount("", AccountType.NONE);
  }
}

function handleDisconnect() {
  console.log("MetaMask is disconnecting");
  // disconnect sometimes triggers on chainChanged and would
  // wrongly disconnect the account
  // FtsoService.setAccount("", AccountType.NONE);
}

export default ConnectWallet;
